import { useEffect, useRef } from "preact/hooks";
import type { ComponentChildren } from "preact";

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ComponentChildren;
  footer?: ComponentChildren;
  maxWidth?: string;
  className?: string;
}

/** Reusable, accessible modal. Focus-trapped, restores focus on close,
 * closes on Escape and overlay click. */
export function Modal({ open, onClose, title, children, footer, maxWidth, className }: Props) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previousFocus.current = document.activeElement as HTMLElement;
    // Focus first focusable child.
    const overlay = overlayRef.current;
    if (overlay) {
      const focusable = overlay.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.classList.add("modal-open");
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.classList.remove("modal-open");
      previousFocus.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      class="modal-overlay active"
      ref={overlayRef}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div
        class={`modal-content${className ? ` ${className}` : ""}`}
        style={maxWidth ? { maxWidth } : undefined}
      >
        <div class="modal-header">
          <h2>{title}</h2>
          <button class="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div class="modal-body">{children}</div>
        {footer && <div class="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}
